import {
  PageHeader,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ezhire/ui';
import { Link, useLocation } from '@tanstack/react-router';
import { Suspense, type ComponentType, type ReactNode } from 'react';

export interface ModuleWorkspaceTab {
  id: string;
  label: string;
  Component: ComponentType;
  /** Tab is selectable via ?tab=<id> but hidden from the visible tab strip. */
  hiddenInTabStrip?: boolean;
}

interface ModuleWorkspaceTabsProps {
  title: string;
  subtitle?: string;
  breadcrumb?: string;
  tabs: ModuleWorkspaceTab[];
  /** Tab id to render when ?tab= is missing or unrecognized. */
  defaultTab: string;
  /** Path the tab strip lives under (e.g. `/modules/call-center/calls`). */
  basePath: string;
  /** Optional content rendered between the page header and the tab strip
   *  (e.g. a sub-channel selector for Marketing → Channels). */
  prefix?: ReactNode;
}

/**
 * Tab strip for a module Workspace. Reads `?tab=` from the URL and
 * renders the active tab's component (lazy-aware via Suspense).
 *
 * Built on `@ezhire/ui` Radix-backed `Tabs` primitives but stays
 * URL-driven: each `TabsTrigger` is `asChild` a TanStack `Link` so
 * cmd-click / right-click / bookmarking all work the way users expect.
 * The `Tabs` `value` prop mirrors the URL-derived active id; URL is
 * the single source of truth (no `onValueChange` handler — clicks
 * navigate the Link, URL change re-renders with new `value`, keyboard
 * Enter on a focused trigger activates the Link nav).
 *
 * Sibling of `features/control-panel/workspace-tabs.tsx` and
 * `features/settings-shell/workspace-tabs.tsx` (those use TanStack
 * `<Outlet/>` because Settings + Control Panel are file-based routes).
 * Modules dispatch internally via pathname + search params, so this
 * component renders the active `TabsContent` directly instead.
 *
 * Sits in @ezhire/sdk because every module's Workspace shell uses it —
 * keeping it in apps/web would force a forbidden cross-package import.
 */
export function ModuleWorkspaceTabs({
  title,
  subtitle,
  breadcrumb,
  tabs,
  defaultTab,
  basePath,
  prefix,
}: ModuleWorkspaceTabsProps) {
  const location = useLocation();
  const search = (location.search as Record<string, string | undefined>) ?? {};

  // Resolution order: requested `?tab=` if it matches a real id, else
  // `defaultTab` if IT matches, else the first tab. Keeps the visible-tab
  // strip in sync with the rendered component even when a caller passes a
  // defaultTab that isn't in `tabs` (or the search param is garbage).
  const requestedTab = typeof search.tab === 'string' ? search.tab : undefined;
  const requestedHit = tabs.find((t) => t.id === requestedTab);
  const defaultHit = tabs.find((t) => t.id === defaultTab);
  const resolvedTab = requestedHit ?? defaultHit ?? tabs[0];
  const activeId = resolvedTab?.id ?? '';

  const visibleTabs = tabs.filter((t) => !t.hiddenInTabStrip);

  return (
    <div className="flex min-w-0 flex-col">
      <PageHeader title={title} subtitle={subtitle} breadcrumb={breadcrumb} />
      {prefix}
      {/* `activationMode="manual"` is load-bearing: this `Tabs` is
          controlled by URL (`value={activeId}` with no `onValueChange`)
          and activation happens via the `asChild Link` navigating, NOT
          via Radix's internal state. In the default automatic mode,
          arrow-key focus changes would call `onValueChange` (which
          doesn't exist) to switch tabs, so keyboard tab-switching
          silently breaks. Manual mode makes arrow keys focus-only and
          Enter/Space activates the focused `Link` — restoring keyboard
          parity with click (Codex P2 #3292150585 on PR #386). */}
      <Tabs value={activeId} activationMode="manual" className="mt-2">
        {/* `w-full` + `flex-wrap` + `h-auto` lets the strip fill the
            container and wrap onto multiple rows when the Workspace
            has many tabs (e.g. Settings shell with 13 tabs). The base
            `TabsList` style is `inline-flex h-10` which doesn't wrap. */}
        <TabsList aria-label={`${title} tabs`} className="w-full flex-wrap h-auto">
          {visibleTabs.map((t) => {
            // Default tab gets a clean URL (no ?tab=). Other tabs carry
            // ?tab=<id>. Other search params on the URL are preserved so
            // 2-level Workspaces (Marketing → Channels) keep their outer
            // ?ch= state when the inner tab changes.
            //
            // EXCEPT detail-autoload keys (campaign / influencer / call /
            // discussion / brief / run): those belong to a specific tab's
            // detail page and would survive tab navigation as stale
            // entity IDs, causing the next tab to auto-open the wrong
            // detail (Codex P2 #3292150584 on PR #386). Strip them on
            // every tab transition.
            const nextSearch: Record<string, unknown> = { ...search };
            delete nextSearch.campaign;
            delete nextSearch.influencer;
            delete nextSearch.call;
            delete nextSearch.discussion;
            delete nextSearch.brief;
            delete nextSearch.run;
            if (t.id === defaultTab) delete nextSearch.tab;
            else nextSearch.tab = t.id;
            return (
              <TabsTrigger key={t.id} value={t.id} asChild>
                <Link to={basePath as never} search={nextSearch as never} viewTransition className="relative data-[state=active]:border-transparent">
                  {t.label}
                  {t.id === activeId ? <span className="atlas-tab-underline" style={{ viewTransitionName: `atlas-tab-module-${basePath.replace(/\W+/g, '-')}` }} aria-hidden="true" /> : null}
                </Link>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {/* One <Suspense> per <TabsContent> so each lazy panel loads only
            when active. Radix `TabsContent` only mounts content when
            its `value` matches the Tabs `value`, so the unmounted
            panels don't suspend until activated. */}
        {tabs.map((t) => (
          <TabsContent key={t.id} value={t.id} className="atlas-page-enter">
            <Suspense fallback={<TabFallback />}>
              <t.Component />
            </Suspense>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function TabFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner className="h-5 w-5 text-text-muted" />
    </div>
  );
}
